import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { isProtectedPath, PROTECTED_PATHS } from 'state/modules/verification';

// Only strip a leading 2-3 letter locale segment (e.g. "/en", "/ja"). Naive
// [a-z]+ was greedy and ate first-word paths like "/security" too.
const stripLocale = pathname => (pathname || '').replace(/^\/[a-z]{2,3}(?=\/|$)/i, '') || '/';

const RenderRoute = ({ locale, isAdmin, isUser, route, tenantId, userAllowedTenants }) => {
  // useLocation replaces the location prop that Route render-props supplied in v5.
  const location = useLocation();
  // OWN-224: step-up is session-wide, not per-page (finalized on the ticket
  // after discussion - a single verification this session covers every
  // protected page, not just the one it was originally requested for).
  const sessionVerified = useSelector(state => state.auth?.sessionVerified);
  const hasPasskey = useSelector(state => state.auth?.hasPasskey);

  // If it's a protected route and it's an Admin but has not selected a user
  if (route.private && isAdmin && !isUser) {
    return <Navigate to={`/${locale}/selectOwnerId?nextURL=${location.pathname}`} replace />;
  }

  // If the user is protected and the user is not authenticated
  if (route.private && !isUser) {
    const nextUrl = location.pathname ? `nextURL=${location.pathname}` : '';
    // Include the URL search queries in the Login URL, it's needed when login in with Google
    const search = location.search ? `${location.search}&${nextUrl}` : `?${nextUrl}`;
    return <Navigate to={`/${locale}/login${search}`} replace />;
  }

  // FTR-3/OWN-223 hard block is NOT handled here (deliberately, not a gap) - it's a
  // non-dismissible modal in App.js, overlaid on whatever route is already active,
  // rather than a redirect to a dedicated page/route. See App.js's
  // showPasskeyMandatory.

  // If the route is only for administrator and the user is not an administrator
  if (route.admin && !isAdmin) {
    return <Navigate to={`/${locale}/login`} replace />;
  }

  if (tenantId && route.excludeTenant === tenantId) {
    return <Navigate to={`/${locale}/comingsoon`} replace />;
  }

  if (route.requiredTenantId && !userAllowedTenants.includes(route.requiredTenantId)) {
    return <Navigate to={`/${locale}/accessdenied`} replace />;
  }

  if (route.redirectTo && typeof route.redirectTo === 'string') {
    return <Navigate to={route.redirectTo} replace />;
  }

  // OWN-224: step-up guard for the three protected pages. If sessionVerified
  // isn't set yet, route the owner through /verify (with next= carrying the
  // intended destination) rather than mounting the component. Once set, it
  // covers every protected page for the rest of the session - no re-prompt
  // per page and no re-prompt on revisiting. FTR-3 above still takes
  // precedence — owners without a passkey past the deadline never reach
  // this point.
  //
  // Bootstrap exception: an owner with no passkey enrolled yet has no way to
  // satisfy step-up for Security & Login at all - passkey step-up requires an
  // existing passkey, and recovery codes aren't issued until the first
  // passkey is created (chicken-and-egg). owner-api's assertCanManagePasskeys
  // already exempts this exact case (creating the very first passkey needs
  // only the normal login session, not step-up), so skip the guard here too.
  const strippedPath = stripLocale(location.pathname);
  const isBootstrappingFirstPasskey = strippedPath === PROTECTED_PATHS.SECURITY && hasPasskey === false;
  // OWN-229: an admin acting on behalf of a selected owner (isAdmin && isUser)
  // must skip this gate entirely. sessionVerified/hasPasskey above reflect the
  // admin's OWN login, never the selected owner's, so this guard would
  // otherwise redirect to /verify for every owner regardless of that owner's
  // actual passkey/2FA state. Backend authorization remains the real boundary
  // here — see bank-account-verify.interceptor.ts's isAdmin(owner) bypass —
  // this frontend gate is a step-up UX convenience for real owners only.
  if (isUser && !isAdmin && isProtectedPath(location.pathname) && !isBootstrappingFirstPasskey && !sessionVerified) {
    const nextParam = encodeURIComponent(location.pathname + (location.search || ''));
    return <Navigate to={`/${locale}/verify?next=${nextParam}`} replace />;
  }

  return (
    <route.component
      route={route}
      location={location}
    />
  );
};

RenderRoute.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  isUser: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  route: PropTypes.object.isRequired,
  tenantId: PropTypes.string,
  userAllowedTenants: PropTypes.arrayOf(PropTypes.number),
};

export default RenderRoute;
